import { useCallback, useEffect, useState } from "react";
import {
  adminAdfRunsList, adminAdfRunRefresh, type AdfRun,
} from "../api/client";

const PAGE_SIZE = 50;

const STATUS_LABEL: Record<string, string> = {
  Succeeded: "Succeeded",
  Failed: "Failed",
  Cancelled: "Cancelled",
  TimedOut: "Timed Out",
  TriggerFailed: "Trigger Failed",
  InProgress: "In Progress",
  Queued: "Queued",
};

function statusVariant(status: string): string {
  switch (status) {
    case "Succeeded": return "success";
    case "Failed":
    case "TimedOut":
    case "TriggerFailed": return "danger";
    case "Cancelled": return "warning";
    case "InProgress": return "info";
    case "Queued": return "neutral";
    default: return "neutral";
  }
}

const SYNC_LABEL: Record<string, string> = {
  success: "sync OK",
  failed: "sync KO",
  skipped: "skipped",
};

function syncVariant(syncStatus: string): string {
  switch (syncStatus) {
    case "success": return "success";
    case "failed": return "danger";
    case "skipped": return "neutral";
    default: return "neutral";
  }
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function summarizeRowsChanged(rows: AdfRun["rows_changed"]): string {
  if (!rows || Object.keys(rows).length === 0) return "—";
  const parts: string[] = [];
  for (const [table, op] of Object.entries(rows)) {
    if (table.startsWith("_")) continue;
    const ups = op.upsert || 0;
    const dels = op.delete || 0;
    const errs = op.errors || 0;
    if (ups + dels + errs === 0) continue;
    const segs = [];
    if (ups) segs.push(`+${ups}`);
    if (dels) segs.push(`-${dels}`);
    if (errs) segs.push(`!${errs}`);
    parts.push(`${table} ${segs.join(" ")}`);
  }
  return parts.length ? parts.join(", ") : "nessun cambiamento";
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`badge badge-${statusVariant(status)}`}>{STATUS_LABEL[status] || status}</span>;
}

function SyncBadge({ syncStatus }: { syncStatus: string | null }) {
  if (!syncStatus) return <span className="text-muted">—</span>;
  return <span className={`badge badge-${syncVariant(syncStatus)}`}>{SYNC_LABEL[syncStatus] || syncStatus}</span>;
}

export function AdminAzureSync() {
  const [runs, setRuns] = useState<AdfRun[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [pendingTriggerSince, setPendingTriggerSince] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminAdfRunsList(PAGE_SIZE, offset);
      setRuns(r.items);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore caricamento");
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    load();
  }, [load]);

  // Polling automatico: ogni 5s se c'è un run InProgress/Queued OPPURE se siamo
  // in attesa post-trigger (subprocess in startup, ~6-9s prima che la riga appaia).
  useEffect(() => {
    const hasActive = runs.some(r => ["InProgress", "Queued"].includes(r.status));
    const waitingForTrigger = pendingTriggerSince !== null
      && Date.now() - pendingTriggerSince < 60_000;
    if (!hasActive && !waitingForTrigger) return;

    const intervalMs = waitingForTrigger ? 3_000 : 15_000;
    const id = setInterval(() => {
      load();
      // Se vediamo una nuova riga creata dopo il trigger, smettiamo l'attesa speciale
      if (pendingTriggerSince && runs.some(r =>
        new Date(r.started_at).getTime() >= pendingTriggerSince
      )) {
        setPendingTriggerSince(null);
      }
    }, intervalMs);
    return () => clearInterval(id);
  }, [runs, load, pendingTriggerSince]);

  async function onTriggerRefresh() {
    setRefreshing(true);
    setError("");
    setInfo("");
    try {
      await adminAdfRunRefresh();
      const triggerTs = Date.now();
      setPendingTriggerSince(triggerTs);
      setInfo(
        "Trigger inviato — ADF sta partendo, la riga apparirà tra 5-10 secondi " +
        "(la pipeline durerà ~5-15 minuti)."
      );
      // Carica subito + di nuovo dopo 6s (di solito basta) + dopo 12s (margine)
      setTimeout(load, 1_000);
      setTimeout(load, 6_000);
      setTimeout(load, 12_000);
      // Nascondi messaggio info dopo 15s
      setTimeout(() => {
        setInfo(prev => prev.startsWith("Trigger inviato") ? "" : prev);
      }, 15_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore trigger");
    } finally {
      setRefreshing(false);
    }
  }

  const lastSuccess = runs.find(r => r.status === "Succeeded" && r.sync_status === "success");

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1400 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sync Azure Data Factory</h1>
          <p className="text-muted">
            Storico esecuzioni della pipeline <code>pipeline_copy_trunkate_all</code>
            {" "}e del sync event-driven downstream.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={onTriggerRefresh}
          disabled={refreshing}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {refreshing ? "hourglass_top" : "cloud_sync"}
          </span>
          {refreshing ? "Avvio…" : "Esegui ora"}
        </button>
      </div>

      {lastSuccess && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          Ultimo sync riuscito: <strong>{fmtDate(lastSuccess.started_at)}</strong>
          {" "}— ADF {fmtDuration(lastSuccess.duration_s)} + sync {fmtDuration(lastSuccess.sync_duration_s)}
          {" "}— {summarizeRowsChanged(lastSuccess.rows_changed)}
        </div>
      )}

      {info && (
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          {info}
        </div>
      )}

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 16 }}>
          <span className="material-symbols-outlined">warning</span>
          {error}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Avvio</th>
              <th>Trigger</th>
              <th>Status ADF</th>
              <th>Durata</th>
              <th>Sync</th>
              <th>Modifiche</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && runs.length === 0 ? (
              <tr><td colSpan={7} className="text-center" style={{ padding: "var(--space-5)" }}>Caricamento…</td></tr>
            ) : runs.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted" style={{ padding: "var(--space-5)" }}>
                Nessuna esecuzione registrata. Premi "Esegui ora" per la prima sync.
              </td></tr>
            ) : (
              runs.map(r => (
                <RunRow
                  key={r.id} run={r}
                  expanded={expandedId === r.id}
                  onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="pagination">
          <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}>
            ← Precedenti
          </button>
          <span style={{ alignSelf: "center" }}>{offset + 1}–{Math.min(offset + PAGE_SIZE, total)} di {total}</span>
          <button onClick={() => setOffset(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total}>
            Successivi →
          </button>
        </div>
      )}
    </div>
  );
}

function RunRow({ run, expanded, onToggle }: { run: AdfRun; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <tr onClick={onToggle}>
        <td>{fmtDate(run.started_at)}</td>
        <td><code style={{ fontSize: "0.85em" }}>{run.triggered_by}</code></td>
        <td><StatusBadge status={run.status} /></td>
        <td>{fmtDuration(run.duration_s)}</td>
        <td>
          <SyncBadge syncStatus={run.sync_status} />
          {run.sync_duration_s != null && (
            <span className="text-muted" style={{ marginLeft: 6, fontSize: "0.85em" }}>
              {fmtDuration(run.sync_duration_s)}
            </span>
          )}
        </td>
        <td title={JSON.stringify(run.rows_changed)}>{summarizeRowsChanged(run.rows_changed)}</td>
        <td>
          <span className="material-symbols-outlined">{expanded ? "expand_more" : "chevron_right"}</span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: "0.85em" }}>
              <div>
                <strong>Run ID:</strong> <code>{run.id}</code><br />
                <strong>Pipeline:</strong> {run.pipeline_name}<br />
                <strong>Avvio:</strong> {fmtDate(run.started_at)}<br />
                <strong>Fine:</strong> {fmtDate(run.finished_at)}<br />
                {run.error_message && (
                  <div className="alert alert-danger" style={{ marginTop: 8, flexDirection: "column", alignItems: "flex-start" }}>
                    <strong>Errore ADF:</strong>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{run.error_message}</pre>
                  </div>
                )}
              </div>
              <div>
                <strong>Sync downstream:</strong> {run.sync_status || "—"}<br />
                {run.sync_started_at && <><strong>Sync avvio:</strong> {fmtDate(run.sync_started_at)}<br /></>}
                {run.sync_duration_s != null && <><strong>Sync durata:</strong> {fmtDuration(run.sync_duration_s)}<br /></>}
                {run.rows_changed && Object.keys(run.rows_changed).length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <strong>Righe modificate:</strong>
                    <pre style={{ background: "var(--indaco-white)", padding: 8, borderRadius: 4, marginTop: 4, fontSize: "0.85em" }}>
                      {JSON.stringify(run.rows_changed, null, 2)}
                    </pre>
                  </div>
                )}
                {run.sync_error && (
                  <div className="alert alert-danger" style={{ marginTop: 8, flexDirection: "column", alignItems: "flex-start" }}>
                    <strong>Errore sync:</strong>
                    <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{run.sync_error}</pre>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
